<template>
  <!-- Table Container Card -->
  <b-card
    no-body
  >

    <div class="m-2">

      <!-- Table Top -->
      <b-row>

        <!-- Per Page -->
        <b-col
          cols="12"
          md="6"
          class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
        >
          <TableEntries
            v-model="perPage"
            :options="perPageOptions"
          />
          <Button
            :label="$t('Post')"
            size="md"
            class="mt-0"
            @click="goToAddCashDeposit"
          />
        </b-col>

        <!-- Search -->
        <b-col
          cols="12"
          md="6"
        >
          <div class="d-flex align-items-center justify-content-lg-end">
            <Button
              :label="$t('Filter')"
              size="md"
              icon="FilterIcon"
              show-icon
              class="mr-1"
              @click="showModal('modal-filter')"
            />
            <Button
              :label="$t('Reset Filter')"
              size="md"
              icon="RotateCwIcon"
              show-icon
              @click="resetFilter"
            />
          </div>
        </b-col>
      </b-row>

    </div>

    <b-table
      ref="customerInfoRef"
      :items="cachedCashDepositList ? cachedCashDepositList.data : cashDepositList"
      responsive
      :busy="isBusy"
      :per-page="pageSize"
      :current-page="currentPage"
      :fields="translatedTableHeaders"
      primary-key="id"
      show-empty
      :empty-text="$t('No records available')"
      class="position-relative"
    >
      <template #table-busy>
        <div class="text-center text-danger my-2">
          <b-spinner
            class="align-middle"
            variant="primary"
          />
        </div>
      </template>

      <template #cell(SN)="data">
        {{ getOverallIndex(data.index) }}
      </template>

      <template #cell(financialDate)="data">
        {{ toDateWithYear(data.value) }}
      </template>

      <template #cell(transactionDate)="data">
        {{ toDateWithTime(data.value) }}
      </template>

      <template #cell(amount)="data">
        {{ toBalance(data.value) }}
      </template>

      <!-- Column: Actions -->
      <template #cell(actions)="data">
        <feather-icon
          icon="EyeIcon"
          class="cursor-pointer"
          @click="onViewCashDeposit(data.item)"
        />
      </template>

    </b-table>

    <TablePagination
      v-model="currentPage"
      :from="from"
      :to="to"
      :total-rows="totalEntries"
      :per-page="perPage"
    />

    <!-- modal -->
    <b-modal
      id="modal-lg"
      ref="my-modal"
      :title="$t('Cash Deposit')"
      :ok-title="$t('Print Receipt')"
      no-close-on-backdrop
      :cancel-title="$t('Cancel')"
      cancel-variant="outline-secondary"
      centered
      size="lg"
      hide-footer
      modal-footer
    >
      <div class="text-center">
        <h3> {{ $t('Posting Reference:') }} {{ cashDepositData.postingReferenceNumber }}</h3>
      </div>

      <TableEntries
        v-model="transactionPerPage"
        :options="perPageOptions"
      />

      <b-table
        ref="cashDepositRef"
        :items="transactionList"
        responsive
        :busy="isBusy"
        :per-page="transactionPageSize"
        :current-page="transactionCurrentPage"
        :fields="translatedTransactionTableHeaders"
        primary-key="id"
        show-empty
        :empty-text="$t('No records available')"
        class="position-relative mt-3"
      >
        <template #table-busy>
          <div class="text-center text-danger my-2">
            <b-spinner
              class="align-middle"
              variant="primary"
            />
          </div>
        </template>

        <template #cell(SN)="data">
          {{ getOverallIndex(data.index) }}
        </template>

      </b-table>
      <TablePagination
        v-model="transactionCurrentPage"
        :from="transactionFrom"
        :to="transactionTo"
        :total-rows="transactionTotalEntries"
        :per-page="transactionPerPage"
      />
      <hr>
      <div class="d-flex align-items-center justify-content-lg-end">
        <Button
          :label="$t('Cancel')"
          size="md"
          class="mr-1"
          variant="outline-secondary"
          @click="cancelButton"
        />
        <Button
          :label="$t('POS Printer')"
          size="md"
          icon="PrinterIcon"
          show-icon
          class="mr-1"
          @click="posPrintReceipt"
        />
        <Button
          :label="$t('Billing Printer')"
          size="md"
          icon="PrinterIcon"
          show-icon
          @click="billingPrintReceipt"
        />
      </div>
    </b-modal>
    <!-- modal -->

    <!-- Filter modal -->
    <b-modal
      id="modal-filter"
      ref="my-modal"
      :ok-title="$t('Apply')"
      no-close-on-backdrop
      :cancel-title="$t('Cancel')"
      cancel-variant="outline-secondary"
      centered
      size="lg"
      :title="$t('Filters')"
      modal-footer
      @ok="fetchCashDeposit"
      @cancel="resetFilters"
    >
      <b-row class="match-height">
        <!-- Account Name -->
        <b-col md="6">
          <InputField
            id="account-name"
            v-model="filter.accountName"
            :label="$t('Account Name')"
            :placeholder="$t('Enter account name')"
            type="text"
            onpaste="return false;"
            :is-allow-special-characters="false"
          />
        </b-col>
        <!-- Account Name -->

        <!-- Account Number -->
        <b-col md="6">
          <InputField
            id="account-number"
            v-model="filter.accountNumber"
            :label="$t('Account Number')"
            :placeholder="$t('Enter account number')"
            type="text"
          />
        </b-col>
        <!-- Account Number -->

        <!--Posting Reference Number -->
        <b-col md="6">
          <InputField
            id="reference-number"
            v-model="filter.postingReferenceNumber"
            :label="$t('Reference Number')"
            :placeholder="$t('Enter posting reference number')"
            type="text"
          />
        </b-col>
        <!-- Posting Reference Number -->

        <!-- Instrument Number -->
        <b-col md="6">
          <InputField
            id="instrument-number"
            v-model="filter.instrumentNumber"
            :label="$t('Instrument Number')"
            :placeholder="$t('Enter instrument number')"
            type="text"
            for="instrument-number"
          />
        </b-col>
        <!-- Instrument Number -->

        <!-- Entry Number -->
        <b-col md="6">
          <InputField
            id="entry-number"
            v-model="filter.entryCode"
            :label="$t('Entry Number')"
            :placeholder="$t('Enter entry number')"
            type="number"
          />
        </b-col>
        <!-- Entry Number -->

        <!-- GL code -->
        <b-col md="6">
          <InputField
            id="gl-code"
            v-model="filter.glCode"
            :label="$t('GL Code')"
            :placeholder="$t('Enter gl code')"
            type="text"
          />
        </b-col>
        <!-- GL code -->

        <!-- Branch -->
        <b-col md="6">
          <SelectField
            id="branch"
            v-model="filter.branchCode"
            :label="$t('Branch')"
            :placeholder="$t('Select Branch')"
            :options="branchOptions"
            :reduce="option => option.value"
            item="label"
          />
          <!-- Branch -->
        </b-col>

        <!-- Financial Date From -->
        <b-col md="6">
          <DatePickerField
            id="date-of-public-holiday"
            v-model="filter.financialDateFrom"
            :label="$t('Financial Date')"
            :config="datePickerConfig"
          />
        </b-col>
        <!-- Financial Date From-->

        <!-- Financial Date To -->
        <b-col md="6">
          <DatePickerField
            id="date-of-public-holiday"
            v-model="filter.financialDateTo"
            :label="$t('Financial Date')"
            :config="datePickerConfig"
          />
        </b-col>
        <!-- Financial Date To-->

        <!-- Start Date -->
        <b-col md="6">
          <DatePickerField
            id="start-date"
            v-model="filter.startDate"
            :label="$t('Start Date')"
            :config="datePickerConfig"
          />
        </b-col>
        <!-- Start Date -->

        <!-- End Date -->
        <b-col md="6">
          <DatePickerField
            id="end-date"
            v-model="filter.endDate"
            :label="$t('End Date')"
            :config="datePickerConfig"
          />
        </b-col>
        <!-- End Date -->
      </b-row>
    </b-modal>
    <!-- Filter modal -->

    <!-- Print Transaction Receipt -->
    <div
      id="transaction-receipt-print"
    >
      <p id="prints">

        <b-card>
          <b-row
            no-gutters
          >
            <b-col
              md="12"
              style="text-align: center; margin-top: -70px;"
            >
              <img
                :src="userDetails.institutionLogoURL ? userDetails.institutionLogoURL : ''"
              >
              <p
                v-if="corporateDetails"
                style="font-size: 3rem; text-align: center;"
                class="mt-2"
              >
                {{ corporateDetails.registeredAddress }}
              </p>
              <p
                style="font-family: 'Gill Sans Extrabold', sans-serif; font-size: 4rem; margin-bottom: 4rem; font-weight: 600; text-align: center;"
                class="mt-4"
              >
                {{ $t('Transaction Receipt') }}
              </p>
            </b-col>
          </b-row>
          <b-row no-gutters>
            <b-col
              md="8"
              :style="printerFirstStyle"
            >
              <div class="watermark">
                <b-card-body>
                  <b-card-text>
                    <div class="watermark__inner">
                      <div class="watermark__body">
                        {{ corporateDetails.registeredName }}
                      </div>
                    </div>
                    <table
                      :style="printerSecondStyle"
                      style="display: table; table-layout: fixed;"
                    >
                      <tbody>
                        <tr>
                          <td style="font-size: 3rem; padding-top: 5rem; color:#000;">
                            {{ $t('Transaction Type') }}
                          </td>
                          <td style="text-align: right; font-size: 3rem; padding-top: 5rem; font-weight: 700; color:#000">
                            {{ $t('Cash Deposit') }}
                          </td>
                        </tr>
                        <tr>
                          <td style="font-size: 3rem; color:#000; padding-top: 5rem;">
                            {{ $t('Account Number') }}
                          </td>
                          <td style="text-align: right; font-size: 3rem; padding-top: 5rem; color:#000; font-weight: 600; text-transform: capitalize; line-height: 1;">
                            {{ cashDepositData.accountNumber }}
                          </td>
                        </tr>
                        <tr>
                          <td style="font-size: 3rem; color:#000; padding-top: 5rem;">
                            {{ $t('Account Name') }}
                          </td>
                          <td style="text-align: right; font-size: 3rem; padding-top: 5rem;  font-weight: 600;  color:#000; line-height: 1;">
                            {{ cashDepositData.accountName }}
                          </td>
                        </tr>
                        <tr>
                          <td style="font-size: 3rem; padding-top: 5rem; color:#000;">
                            {{ $t('Amount') }}
                          </td>
                          <td style="text-align: right; font-size: 3rem; padding-top: 5rem; font-weight: 700; color:#000; line-height: 1;">
                            {{ toBalance(cashDepositData.amount) }}
                          </td>
                        </tr>
                        <tr>
                          <td style="font-size: 3rem; color:#000; padding-top: 5rem;">
                            {{ $t('Branch') }}
                          </td>
                          <td style="text-align: right; font-size: 3rem; padding-top: 5rem; font-weight: 600; color:#000; line-height: 1;">
                            {{ cashDepositData.branch }}
                          </td>
                        </tr>
                        <tr>
                          <td style="font-size: 3rem; padding-top: 5rem; color:#000;">
                            {{ $t('Instrument Number') }}
                          </td>
                          <td style="text-align: right; font-size: 3rem; padding-top: 5rem; font-weight: 600; color:#000; line-height: 1;">
                            {{ cashDepositData.instrumentNumber }}
                          </td>
                        </tr>
                        <tr>
                          <td style="font-size: 3rem; padding-top: 5rem; color:#000;">
                            {{ $t('Posting Reference') }}
                          </td>
                          <td style="text-align: right; font-size: 3rem; padding-top: 5rem; font-weight: 600; color:#000; line-height: 1;">
                            {{ cashDepositData.postingReferenceNumber }}
                          </td>
                        </tr>
                        <tr>
                          <td style="font-size: 3rem; padding-top: 5rem; color:#000;">
                            {{ $t('Details') }}
                          </td>
                          <td style="text-align: right; font-size: 3rem; padding-top: 5rem; font-weight: 600; color:#000; line-height: 1;">
                            {{ cashDepositData.narration }}
                          </td>
                        </tr>
                        <tr>
                          <td style="font-size: 3rem; padding-top: 5rem; color:#000;">
                            {{ $t('Teller') }}
                          </td>
                          <td style="text-align: right; font-size: 3rem; padding-top: 5rem; font-weight: 600; color:#000; line-height: 1;">
                            {{ cashDepositData.initiator }}
                          </td>
                        </tr>
                        <tr>
                          <td style="font-size: 3rem; padding-top: 5rem; color:#000;">
                            {{ $t('Transaction Date') }}
                          </td>
                          <td style="text-align: right; font-size: 3rem; padding-top: 5rem; font-weight: 600; color:#000; line-height: 1;">
                            {{ cashDepositData.transactionDate ? cashDepositData.transactionDate : '' }}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </b-card-text>
                </b-card-body>
              </div>
            </b-col>
          </b-row>
        </b-card>

      </p>
    </div>
    <!-- Print Transaction Receipt -->

    <FullScreenSpinner :show="showSpinner" />
  </b-card>

</template>

<script>
import {
  BCard, BCardBody, BCardText, BRow, BCol, BTable,
  BSpinner,
} from 'bootstrap-vue'
import { modalMixins } from '@core/mixins/modal'
import { tableMixins } from '@core/mixins/table'
import { sweetAlert } from '@core/mixins/sweetAlert'
import { mapActions, mapGetters } from 'vuex'
import TablePagination from '@/views/components/table/TablePagination.vue'
import { toAmount, toBalance } from '@/helpers/formatAmount'
import TableEntries from '@/views/components/table/TableEntries.vue'
import FullScreenSpinner from '@/views/components/spinner/FullScreenSpinner.vue'
import { toDateWithYear, toDateWithTime } from '../../../../../helpers/formatDate'
import InputField from '../../../../forms/InputField.vue'
import SelectField from '../../../../forms/SelectField.vue'
import DatePickerField from '../../../../forms/DatePickerField.vue'
import Button from '../../../../components/button/Button.vue'

export default {
  components: {
    BCard,
    BCardBody,
    BCardText,
    BRow,
    BCol,
    Button,
    BSpinner,
    BTable,
    TablePagination,
    TableEntries,
    InputField,
    SelectField,
    FullScreenSpinner,
    DatePickerField,
  },
  mixins: [modalMixins, tableMixins, sweetAlert],
  data() {
    return {
      toDateWithYear,
      toDateWithTime,
      toAmount,
      toBalance,
      tableHeaders: [
        { key: 'SN', label: 'S/N' },
        { key: 'accountNumber', sortable: true, label: 'Account Number' },
        { key: 'accountName', sortable: true, label: 'Account Name' },
        { key: 'branch', sortable: true, label: 'Branch' },
        { key: 'financialDate', sortable: true, label: 'Financial Date' },
        { key: 'transactionDate', sortable: true, label: 'Transaction Date' },
        { key: 'postingReferenceNumber', sortable: true, label: 'Posting Reference' },
        { key: 'amount', sortable: true, label: 'Amount' },
        { key: 'entryCode', sortable: true, label: 'Sort Code' },
        { key: 'instrumentNumber', sortable: true, label: 'Instrument Number' },
        { key: 'senderName', sortable: true, label: 'Sender Name' },
        { key: 'initiatedBy', sortable: true, label: 'Initiated By' },
        { key: 'narration', sortable: true, label: 'Narration' },
        { key: 'approvedBy', sortable: true, label: 'Approver' },
        { key: 'actions', label: 'Actions' },
      ],
      transactionTableHeaders: [
        { key: 'SN', label: 'S/N' },
        { key: 'accountNumber', sortable: true, label: 'Account Number' },
        { key: 'accountName', sortable: true, label: 'Account Name' },
        { key: 'debit', sortable: true, label: 'Debit' },
        { key: 'credit', sortable: true, label: 'Credit' },
        { key: 'branch', sortable: true, label: 'Branch' },
        { key: 'instrumentNumber', sortable: true, label: 'Instrument Number' },
        { key: 'entryCode', sortable: true, label: 'Sort Code' },
      ],
      categoryFormData: {
        categoryName: '',
        level: 'Select',
        specifyLevel: 'Select',
      },
      cashDepositList: [],
      cashDepositData: {
        accountNumber: '',
        accountName: '',
        branch: '',
        financialDate: '',
        transactionDate: '',
        postingReferenceNumber: '',
        credit: '',
        entryCode: '',
        instrumentNumber: '',
        initiatedBy: '',
        initiator: '',
        narration: '',
        approver: '',
        senderName: '',
      },
      filter: {
        accountName: null,
        accountNumber: null,
        postingReferenceNumber: null,
        instrumentNumber: null,
        branchCode: null,
        financialDateFrom: null,
        financialDateTo: null,
        startDate: null,
        endDate: null,
        entryCode: 'C-CSHD',
        glCode: null,
        postingType: 'CashDepositPosting',
        recordType: null,
      },
      datePickerConfig: {
        dateFormat: 'Y-m-d',
      },
      transactionList: [],
      transactionPerPage: 10,
      transactionPageSize: 0,
      transactionCurrentPage: 1,
      transactionTotalCount: 0,
      showSpinner: false,
      postingReferenceNumber: null,
      corporateDetails: {},
      printerFirstStyle: {
        width: '',
        marginLeft: '',
        marginRight: '',
      },
      printerSecondStyle: {
        width: '',
        marginLeft: '',
        marginRight: '',
      },
    }
  },
  computed: {
    ...mapGetters({
      branchOptions: 'operations/branchOptions',
      userDetails: 'auth/getCurrentUser',
    }),
    ...mapGetters('tableCache', ['getTableCache']),
    cacheKey() {
      // Use page + perPage + filter as cacheKey
      return JSON.stringify({ page: this.currentPage, perPage: this.perPage, filter: this.filter })
    },
    cachedCashDepositList() {
      return this.getTableCache('cash-deposit', this.cacheKey)
    },

    transactionTotalEntries() {
      return this.transactionTotalCount
    },
    transactionNumberOfPages() {
      return Math.floor((this.transactionTotalEntries + this.transactionPerPage - 1) / this.transactionPerPage)
    },
    transactionFrom() {
      if (this.transactionTotalCount === 0) {
        return 0
      }
      return (this.transactionCurrentPage * this.transactionPerPage) - (this.transactionPerPage - 1)
    },
    transactionTo() {
      return Math.min(this.transactionFrom + this.transactionPerPage - 1, this.transactionTotalEntries)
    },
    translatedTableHeaders() {
      return this.tableHeaders.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.key }))
    },
    translatedTransactionTableHeaders() {
      return this.transactionTableHeaders.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.key }))
    },
  },
  watch: {
    currentPage(newValue) {
      this.currentPage = newValue
      this.serveCacheAndFetchCashDeposits()
      //  this.markTableCacheStale('cash-deposit')
    },
    perPage(newValue) {
      this.perPage = newValue
      this.serveCacheAndFetchCashDeposits()
    },
    transactionCurrentPage(newValue) {
      this.transactionCurrentPage = newValue
      this.fetchPostings(this.postingReferenceNumber)
    },
    transactionPerPage(newValue) {
      this.transactionPerPage = newValue
      this.fetchPostings(this.postingReferenceNumber)
    },
  },
  mounted() {
    this.serveCacheAndFetchCashDeposits()
    this.fetchCorporateInformation()
  },
  methods: {
    ...mapActions({
      getPostingsReport: 'reports/getTransactionReports',
      getCorporateInformation: 'operations/getCorporateInformationList',
    }),
    ...mapActions('tableCache', ['setTableCache', 'markTableCacheStale', 'clearTableCache']),

    async serveCacheAndFetchCashDeposits() {
      const { cacheKey } = this
      const cache = this.getTableCache('cash-deposit', cacheKey)

      if (cache && cache.data) {
        this.cashDepositList = cache.data
        this.totalCount = cache.totalCount
        this.isBusy = false
        this.fetchCashDeposit(true)
      } else {
        // No cache, show spinner and fetch
        this.isBusy = true
        await this.fetchCashDeposit(false)
      }
    },
    goToAddCashDeposit() {
      this.$router.push('add-cash-deposit')
    },
    async fetchCorporateInformation() {
      try {
        const response = await this.getCorporateInformation({
          permission: 'VIEW_CASH_DEPOSIT_POSTING_LIST',
        })
        const { status, data } = response
        if (status === 200 && data) {
          this.corporateDetails = {
            registeredName: data.data.registeredName,
            registeredAddress: data.data.registeredAddress,
          }
        }
      } catch (error) {
        this.handleError(error)
      }
    },
    async fetchCashDeposit(isBackground = false) {
      if (!isBackground) {
        this.isBusy = true
        this.hideModal('modal-filter')
      }
      try {
        const response = await this.getPostingsReport({
          search: this.filter,
          page: this.currentPage,
          limit: this.perPage,
          route: 'postingsReport',
          permission: 'VIEW_CASH_DEPOSIT_POSTING_LIST',
        })
        const { data } = response
        if (data.data !== null) {
          const cache = this.cachedAccountOfficerList
          const isDifferent = !cache || JSON.stringify(cache.data) !== JSON.stringify(data.data)
          if (isDifferent) {
            this.cashDepositList = data.data
            this.totalCount = data.recordCount
            // If on page 1 and data changed, mark all cash deposit cache as stale
            if (this.currentPage === 1) {
              this.markTableCacheStale('cash-deposit')
            }
          }
          // Always update cache
          this.setTableCache({
            tableKey: 'cash-deposit',
            cacheKey: this.cacheKey,
            payload: {
              data: data.data,
              totalCount: data.recordCount,
              lastFetched: Date.now(),
              stale: false,
            },
          })
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        if (!isBackground) this.isBusy = false
      }
    },
    async fetchPostings(postingReferenceNumber) {
      const response = await this.getPostingsReport({
        search: { postingReferenceNumber },
        page: this.transactionCurrentPage,
        limit: this.transactionPerPage,
        route: 'postingsReport',
        permission: 'VIEW_CASH_DEPOSIT_POSTING_LIST',
      })
      const { data, status } = response
      if (status === 200 && data.data) {
        this.transactionList = data.data.map(item => ({
          ...item,
          credit: toBalance(item.credit),
          debit: toBalance(item.debit),
        }))
        this.transactionTotalCount = data.recordCount
      }
    },
    async onViewCashDeposit(items) {
      try {
        this.showSpinner = true
        this.postingReferenceNumber = items.postingReferenceNumber
        await this.fetchPostings(items.postingReferenceNumber)

        this.cashDepositData = {
          ...items,
          transactionDate: this.toDateWithYear(items.transactionDate),
          financialDate: this.toDateWithYear(items.financialDate),
          credit: toBalance(items.credit),
        }
        this.$root.$emit('bv::show::modal', 'modal-lg')
      } catch (error) {
        this.alertError({ text: error })
      } finally {
        this.showSpinner = false
      }
    },
    posPrintReceipt() {
      this.printerFirstStyle = {
        width: '100%',
        marginLeft: '5%',
        marginRight: '5%',
      }
      this.printerSecondStyle = {
        width: '100%',
        marginLeft: '0%',
        marginRight: '0%',
      }
      // this.printerFirstStyle = {
      //   width: '100%',
      //   marginLeft: '20%',
      //   marginRight: '38%',
      // }
      // this.printerSecondStyle = {
      //   width: '100%',
      //   marginLeft: '20%',
      //   marginRight: '38%',
      // }
      setTimeout(() => {
        window.print()
      }, 200)
    },
    billingPrintReceipt() {
      this.printerFirstStyle = {
        width: '100%',
        marginLeft: '0%',
        marginRight: '0%',
      }
      this.printerSecondStyle = {
        width: '100%',
        marginLeft: '0%',
        marginRight: '0%',
      }
      // this.printerFirstStyle = {
      //   width: '55%',
      //   marginLeft: '10%',
      //   marginRight: '5%',
      // }
      // this.printerSecondStyle = {
      //   width: '100%',
      //   marginLeft: '25%',
      //   marginRight: '25%',
      // }
      setTimeout(() => {
        window.print()
      }, 200)
    },
    cancelButton() {
      this.hideModal('modal-lg')
    },
    resetFilters() {
      this.filter = {
        ...null,
        postingType: 'CashDepositPosting',
        recordType: 'Credit',
      }
      this.markTableCacheStale('cash-deposit')
    },
    resetFilter() {
      this.filter = {
        ...null,
        entryCode: 'C-CSHD',
        postingType: 'CashDepositPosting',
        recordType: 'Credit',
      }
      this.markTableCacheStale('cash-deposit')
      this.fetchCashDeposit(true)
    },

  },
}
</script>

<style lang="scss">
      @media screen {
  #prints {
    display: none;
   }
}

@media print {
   html, body {
     height: 99%;
     page-break-after: avoid !important;
     page-break-before: avoid !important;
     visibility:hidden;
  }
  #transaction-receipt-print, #transaction-receipt-print * {
    visibility: visible; border: none;
  }
  #prints, #prints * {
    visibility:visible;
  }
  #prints {
    position:absolute;
    left:0;
    top:0;
    right: 0;
  }
  img {
  max-width: 20%;
  height: auto;
}
}
.watermark {
    position: relative;
}
.watermark__inner {
    /* Center the content */
    align-items: center;
    display: flex;
    justify-content: center;

    /* Absolute position */
    position: absolute;
    left: 100px;
    top: 0px;
    height: 100%;
    width: 100%;
}

.watermark__body {
    color: rgba($color: #333232, $alpha: 0.3);
    font-size: 5.3rem;
    font-weight: bold;
    white-space: nowrap;
    text-transform: capitalize;
    font-family: "Gill Sans Extrabold", sans-serif !important;
    /* Rotate the text */
    transform: rotate(-60deg);

    /* Disable the selection */
    user-select: none;
}
</style>
